import moment from 'moment-timezone'
import validate from 'validate.js'
import { $User } from '../stores/UserStore/UserStore'
import { SERVICE_FEE_ITEM_NAME, SSPA_AVAILABILITY_ID } from '../../shared-lib/commonStrings'
import type { $ExternalBooking, $ExternalBookingItem } from '../stores/ExternalBookingStore/ExternalBookingModel'
import { defaultCountry, defaultCurrency } from '../../shared-lib/i18n/i18n'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import WebPageModel, { PROTECTED_PATHS } from '../stores/WebPageStore/WebPageModel'
import countries from '../../shared-lib/countryCodes.json'
import { DEFAULT_DIAL_CODE } from './validateMobile'
import { ExternalBookingPaymentStatus, ExternalBookingStatus, PaymentTypes, UpfrontPaymentOption } from '../../graphQL/generated/enums'

export const PRO_PRICES = {
  'gbp': 2999,
  'eur': 2999,
  'aud': 5999,
}
export const GROW_PRICES = {
  'gbp': 1599,
  'eur': 1799,
  'aud': 3499,
}

export interface $SectionListGroup<T> {
  title: string
  data: Array<T>
}

export const groupBy = function (items: Array<any>, key: string, valueMap?: Array<any>): Record<string, any> {
  return (valueMap || items).reduce(function (rv, x, i) {
    (rv[x[key]] = rv[x[key]] || []).push(items[i])
    return rv
  }, {})
}

export function invertObject(obj: Record<string, any>) {
  const invertedObj: Record<string, any> = {}
  for (const [key, value] of Object.entries(obj)) {
    invertedObj[value] = key
  }
  return invertedObj
}

export const createSectionListGroups = function <T>(items: T[], key: string, valueMap?: Array<any>): Array<$SectionListGroup<T>> {
  const groups = groupBy(items, key, valueMap)
  return Object.keys(groups).map((title: string, index: number) => {
    return {
      title,
      data: groups[title] as T[],
      index,
    }
  })
}

// "Mary Watson" -> "Mary W.". The surname is what tells two people apart in a list, so a middle
// name is skipped rather than initialised. A one-word name has nothing to abbreviate.
export const abbreviatedName = (fullName?: string) => {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)

  if (parts.length < 2) return parts[0] ?? ''
  return `${parts[0]} ${parts[parts.length - 1][0]}.`
}

// The same rule for a user record, which carries the name in two fields rather than one.
export const abbreviatedUserName = (user?: Partial<$User>) =>
  abbreviatedName([user?.firstName, user?.lastName].filter(Boolean).join(' '))

export const daysAsString = (days: number) => {
  return days === 7 ? 'seven' : days === 30 ? 'thirty' : days === 90 ? 'ninety' : 'threeSixtyFive'
}

export const periodsForDays = (days: number) => {
  return days === 0 ? [] : [
    { key: 'currentPeriod', dates: [xDaysAgoFormated(days), xDaysAgoFormated(0)] },
    { key: 'previousPeriod', dates: [xDaysAgoFormated(days, days), xDaysAgoFormated(days)] },
  ]
}

export const xDaysAgoFormated = (days: number, from = 0) => {
  const toDate = moment(Date.now()).subtract(from, 'days')
  const check = toDate.subtract(days, 'days').format('YYYY-MM-DD')
  return check
}

export const genderPreferenceLabels = {
  male: 'Only Male',
  female: 'Only Female',
  none: 'Any Gender',
}

export type GenderPreferenceType = keyof typeof genderPreferenceLabels

export const genderPreferenceKeys = Object.keys(genderPreferenceLabels) as Array<GenderPreferenceType>

export const stringToColour = (str: string) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  let colour = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF
    colour += ('00' + value.toString(16)).slice(-2)
  }

  return colour
}

export const formatErrorMessage = (message: string) => {
  if (!message || message.length === 0) return message
  return validate.capitalize(message[0].split(' ').slice(1).join(' '))
}

// Hash function from https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
const cyrb53 = function (str: string, seed = 0) {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return `${4294967296 * (2097151 & h2) + (h1 >>> 0)}`
}

export function generateAutofillId(question: { question: string, type: string, options: string[] }) {
  return cyrb53(`${question.question}:${question.type}:${question.options.join(',')}`)
}

export function businessNameValidation() {
  /*
   *  we use the business name as the statement descriptor in stripe
   *  https://stripe.com/docs/account/statement-descriptors#requirements
   *  we truncate to 22 characters in the API so we don't worry about that one
   */
  return {
    businessName: {
      length: { minimum: 5 },
      format: {
        pattern: /^[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF0-9\s&'\-:!,.]+$/,
        message: 'cannot contain special characters',
      },
    },
  }
}

export function setUndefinedDefaults<T extends Record<string, any>>(data: T, overrides: Partial<T>) {
  for (const key in overrides) {
    if (!data[key]) {
      data[key] = overrides[key]!
    }
  }
}

export const isEmptyOrSSpaAgenda = (locationId: string | undefined | null) => {
  return locationId == null || locationId === SSPA_AVAILABILITY_ID
}

export function generateDarkHexColorFromString(str: string) {
  const char1 = str.charCodeAt(0)
  const char2 = str.charCodeAt(1)

  // Generate RGB values in the lower range for darker colors
  const red = char1 % 76 // Range: 0-75
  const green = char2 % 76 // Range: 0-75
  const blue = (char1 + char2) % 76 // Range: 0-75

  const hex = `#${doubleHex(red)}${doubleHex(green)}${doubleHex(blue)}`

  return hex
}

function doubleHex(num: number, len = 2) {
  const hex = num.toString(16)
  return hex.padStart(len, hex)
}

export function alphaHex(opacity: number) {
  // Scale the opacity to the range of 0 to 255
  const alphaInt = Math.round(opacity * 255)

  // Convert to a two-digit hexadecimal string
  const _alphaHex = alphaInt.toString(16).padStart(2, '0')

  return _alphaHex
}

interface ReferralRewardSetting {
  duration: number
  percentage: number
  from: Date
}

export const latestRewardSettings = (remoteConfig: Record<string, any>) => {
  const referralRewards: ReferralRewardSetting[] = remoteConfig.referralRewards ?? []

  const currentDate = new Date()

  const latestReward = referralRewards
    .filter(reward => reward.from < currentDate)
    .sort((a, b) => a.from.getTime() - b.from.getTime())
    .pop()

  return latestReward ?? { duration: 6, percentage: 80, from: new Date() }
}

export const latestRewardData = (remoteConfig: Record<string, any>, currency = defaultCurrency) => {
  const { duration, percentage } = latestRewardSettings(remoteConfig)

  const totalMaxReward = duration * (percentage / 100) * (PRO_PRICES[currency] ?? PRO_PRICES[defaultCurrency])

  return { totalMaxReward, duration, percentage }
}

export function truncate(str: string, num: number) {
  if (str.length > num) {
    return str.slice(0, num) + '...'
  } else {
    return str
  }
}

export function makeHitSlop(size: number) {
  return {
    left: size,
    right: size,
    top: size,
    bottom: size,
  }
}

export function moveToFirst(arr: string[], itemToMove: string): string[] {
  const index = arr.indexOf(itemToMove)
  if (index > -1) {
    arr.splice(index, 1) // Remove the item from its current position
    arr.unshift(itemToMove) // Insert the item at the beginning of the array
  }
  return arr
}

export const checkForbiddenCharacters = (value: string) => {
  return value.match(/[^a-zA-Z0-9-_]/g)
}

export const replaceSpacesWithDashes = (value: string) => {
  return value.toLowerCase().replace(/ /g, '-')
}

export const handleItemsEditAction = <T extends { _id: string }>(items: Array<T>, action: 'update' | 'add' | 'delete', section: T, sectionId: string) => {
  if (action === 'delete') {
    const index = items.findIndex(item => item._id === sectionId)
    if (index !== -1) {
      items.splice(index, 1)
    }
  } else if (action === 'add') {
    items.push(section)
  } else if (action === 'update') {
    const index = items.findIndex(item => item._id === sectionId)
    if (index !== -1) {
      items[index] = section
    }
  }
}

/**
 * Detects common issues with social media URLs
 * Returns a warning message if an issue is detected, null otherwise
 */
export const checkSocialMediaUrlWarning = (value: string): string | null => {
  if (!value || value.trim() === '') {
    return null
  }

  const trimmedValue = value.trim().toLowerCase()

  // Define social media platform patterns
  const socialPlatforms = [
    { name: 'Instagram', patterns: ['instagram.com'] },
    { name: 'TikTok', patterns: ['tiktok.com'] },
    { name: 'Facebook', patterns: ['facebook.com', 'fb.com'] },
    { name: 'Twitter', patterns: ['twitter.com', 'x.com'] },
    { name: 'LinkedIn', patterns: ['linkedin.com'] },
    { name: 'YouTube', patterns: ['youtube.com', 'youtu.be'] },
    { name: 'Pinterest', patterns: ['pinterest.com'] },
    { name: 'Snapchat', patterns: ['snapchat.com'] },
  ]

  // Check for duplicate URLs (e.g., https://www.instagram.com/https://www.instagram.com/...)
  for (const platform of socialPlatforms) {
    for (const pattern of platform.patterns) {
      const regex = new RegExp(`https?://(www\\.)?${pattern.replace('.', '\\.')}`, 'gi')
      const matches = trimmedValue.match(regex)
      if (matches && matches.length > 1) {
        return 'This URL appears to be invalid. Please check that you\'ve entered the correct URL.'
      }
    }
  }

  // Check for incomplete URLs (missing handle/username)
  for (const platform of socialPlatforms) {
    for (const pattern of platform.patterns) {
      // Check if URL contains the platform domain
      if (trimmedValue.includes(pattern)) {
        // Extract the path after the domain - match domain followed by /, ?, #, or end of string
        const domainRegex = new RegExp(`https?://(www\\.)?${pattern.replace('.', '\\.')}(/|\\?|#|$)`, 'i')
        const match = trimmedValue.match(domainRegex)

        if (match) {
          // Check the last character of the match - if it's ? or #, domain is directly followed by query/hash
          const lastCharOfMatch = match[0][match[0].length - 1]
          if (lastCharOfMatch === '?' || lastCharOfMatch === '#') {
            return `This ${platform.name} URL appears to be incomplete. Please include your ${platform.name} handle (e.g., https://www.${pattern}/yourhandle).`
          }

          const afterDomain = trimmedValue.substring(match.index! + match[0].length)
          // Remove query params and hash for checking
          const pathWithoutParams = afterDomain.split('?')[0].split('#')[0].trim()
          // If path is empty or just a slash, it's incomplete
          if (!pathWithoutParams || pathWithoutParams === '/') {
            return `This ${platform.name} URL appears to be incomplete. Please include your ${platform.name} handle (e.g., https://www.${pattern}/yourhandle).`
          }
          // Check if path looks like it might be incomplete (very short, no alphanumeric)
          if (pathWithoutParams.length < 2 || !/[a-z0-9_-]/.test(pathWithoutParams)) {
            return 'This URL appears to be invalid. Please check that you\'ve entered the correct URL.'
          }
        }
      }
    }
  }

  return null
}

export const validateUrl = (value: string) => {
  if (!value || value.trim() === '') {
    return 'URL cannot be empty'
  }

  const trimmedValue = value.trim()

  const urlRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/gi

  if (!urlRegex.test(trimmedValue)) {
    return 'Please enter a valid URL'
  }

  return true
}

const findDialCode = (isoCountryCode: string) =>
  countries.find(country => country.code === isoCountryCode)?.dialCode

export const getDefaultCountryCode = (isoCountryCode: string = defaultCountry) => {
  return findDialCode(isoCountryCode) ?? findDialCode(defaultCountry) ?? DEFAULT_DIAL_CODE
}

export const validatePhoneNumber = (value: string, countryISOCode: string = defaultCountry) => {
  const countryCodes = countries.map(country => country.dialCode)

  let phoneNumber = value
  phoneNumber = phoneNumber.trim()
  let phoneCountryCode = getDefaultCountryCode(countryISOCode)
  countryCodes.forEach(countryCode => {
    if (countryCode && phoneNumber.startsWith(countryCode)) {
      phoneCountryCode = countryCode
    }
  })

  if (phoneNumber.startsWith(phoneCountryCode)) phoneNumber = phoneNumber.slice(phoneCountryCode.length)
  if (phoneNumber.startsWith('0')) phoneNumber = phoneNumber.slice(1)

  let parsedNumber
  try {
    parsedNumber = parsePhoneNumberWithError(`${phoneCountryCode}${phoneNumber}`)
    if (!parsedNumber.isValid()) throw new Error('Invalid number')
  } catch (e) {
    return ''
  }

  if (!parsedNumber) return ''

  return `${parsedNumber.number}`
}

export const validateNewWebPageTitleAndPath = (title: string, path: string, currentPath: string | undefined, existingPages: WebPageModel[], isEditing = false) => {
  const errors = { title: '', path: '' }

  if (!title || title.length < 3) {
    errors.title = 'Title is required and must be at least 3 characters'
  }
  if (!path || path.length < 3) {
    errors.path = 'Path is required and must be at least 3 characters'
  }

  const forbiddenCharacters = checkForbiddenCharacters(path)
  if (forbiddenCharacters) {
    errors.path = 'Path can only contain alphanumeric characters, dashes, and underscores'
  }

  if (PROTECTED_PATHS.includes(path) && !isEditing) {
    errors.path = 'Cannot be a protected path'
    return errors
  }

  if ((!isEditing || currentPath !== path) && existingPages?.some(page => page.data.path === path)) {
    errors.path = 'A page with this path already exists'
  }

  return errors
}

export const MAX_BUSINESS_NAME_LENGTH = 26 // the length of a single line of text in the navbar on the SW website

// Numeric helpers for incrementor
const clampNumber = (value: number, min = -Infinity, max = Infinity) => {
  return Math.max(Math.min(value, max), min)
}

export function snapToStep(value: number | string, step: number, min?: number, max?: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return clampNumber(Number(min ?? 0), min ?? 0, max)
  if (step <= 0) return clampNumber(parsed, min, max)
  const decimals = Math.max(0, (step.toString().split('.')[1] ?? '').length)
  const snapped = Number((Math.round(parsed / step) * step).toFixed(decimals))
  return clampNumber(snapped, min, max)
}

/*
  Pre-percentage value for solo service fee - 2% covers Stripe's payment processing fees of 1.6% + 30p
  100 was the on / off switch for the solo service fee - now migrated to a percentage value
*/
export const DEFAULT_SOLO_SERVICE_FEE_PERCENTAGE = 2

export function displaySoloServiceFeePercentage(fee?: number | null): number {
  const value = fee ?? 0
  return value === 100 ? DEFAULT_SOLO_SERVICE_FEE_PERCENTAGE : value
}

// Identifies the legacy deposit-flow shape: a booking-portal booking that took a single checkout
// deposit, paid online (Stripe) or with a gift card, still has a balance to collect, isn't
// completed, and carries a legacy "Service Fee" line — i.e. exactly where that fee was genuinely
// collected up front.
export const isLegacyDepositBooking = (booking: $ExternalBooking): $ExternalBookingItem | null => {
  const { depositRequired, source, status, paymentStatus, depositPercentage, payments, items } = booking

  if (depositRequired !== UpfrontPaymentOption.Checkout) return null
  if (source !== 'BOOKING_PORTAL') return null
  if (status === ExternalBookingStatus.Completed) return null
  if (paymentStatus === ExternalBookingPaymentStatus.FullyPaid) {
    return null
  }
  if (!depositPercentage) return null
  if (payments.length > 1) return null // no second payment beyond the deposit

  const hasDeposit = payments.some(p =>
    p.description === 'Deposit' && (p.type === PaymentTypes.Stripe || p.type === PaymentTypes.GiftCard))
  if (!hasDeposit) return null

  return items.find(item => item.name === SERVICE_FEE_ITEM_NAME) ?? null
}

export interface TrialSubscriptionLike {
  trialEnd?: string | Date | null
  inTrial?: boolean | null
}

export const getDaysRemainingInTrial = (subscription: TrialSubscriptionLike | null | undefined) => {
  if (!subscription?.trialEnd || !subscription.inTrial) {
    return 0
  }

  const now = moment()
  const trialEnd = moment(subscription.trialEnd)
  const daysRemaining = trialEnd.diff(now, 'days')

  return Math.max(0, daysRemaining)
}
